/**
 * The sign-in screen. The only route reachable without a session.
 *
 * It says the same thing whatever went wrong, because the server does: a wrong
 * password, an account that does not exist and a deactivated one all answer
 * identically. Anything more helpful here would turn the form into a tool for
 * finding out which addresses are real.
 *
 * The language selector is in the corner beside the theme toggle, and that
 * placement is the point: somebody who cannot read the English form cannot get
 * past it to find a language setting hidden behind sign-in.
 */
import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { HealthBanner } from '../layout/HealthBanner';
import { Logo } from '../components/Logo';
import { StandaloneScreen } from '../components/StandaloneScreen';
import { useAuth } from '../auth/useAuth';
import { ApiError } from '../api/client';

/** Where `RequireAuth` stashed the page the user was trying to open. */
interface LocationState {
  from?: { pathname?: string; search?: string };
}

export default function LoginPage() {
  const { t } = useTranslation(['auth', 'common']);
  const { user, initialising, signIn } = useAuth();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState<string | undefined>(undefined);

  // Signing in is what makes `user` appear, so this doubles as the redirect
  // after a successful attempt — there is no separate navigate call.
  if (user) {
    const state = location.state as LocationState | null;
    const target = `${state?.from?.pathname ?? '/'}${state?.from?.search ?? ''}`;
    return <Navigate to={target} replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setPending(true);
    setFailed(undefined);

    try {
      await signIn(email, password);
    } catch (error) {
      // 401 is the deliberately uninformative answer. Anything else is a real
      // failure — the server being down, say — and saying "check your password"
      // to somebody whose password is fine wastes their afternoon.
      if (error instanceof ApiError && error.status === 401) {
        setFailed(t('auth:signIn.rejected'));
      } else if (error instanceof ApiError) {
        // The backend is not localised; its message is shown as it came rather
        // than swapped for a vaguer one this app happens to have in Czech.
        setFailed(error.message);
      } else {
        setFailed(t('auth:signIn.unreachable'));
      }
      setPending(false);
    }
  }

  if (initialising) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <StandaloneScreen>
      <Stack spacing={3}>
        {/* Here as well as in the shell: with the backend or its database
            down, or the migration that created `sessions` missing, nobody
            gets past this screen to see it. */}
        <HealthBanner />

        <Stack spacing={1.5} sx={{ alignItems: 'center', textAlign: 'center' }}>
          <Logo size={56} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '0.04em' }}>
              {t('common:product.name')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('common:product.tagline')}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('auth:signIn.prompt')}
            </Typography>
          </Box>
        </Stack>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack spacing={2}>
            <TextField
              label={t('auth:signIn.email')}
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              autoComplete="username"
              autoFocus
              required
              fullWidth
              disabled={pending}
            />
            <TextField
              label={t('auth:signIn.password')}
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              fullWidth
              disabled={pending}
            />

            {failed && <Alert severity="error">{failed}</Alert>}

            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={pending || email.trim() === '' || password === ''}
              startIcon={pending ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {pending ? t('auth:signIn.submitting') : t('auth:signIn.submit')}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </StandaloneScreen>
  );
}
